import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button, Popconfirm, message, Switch, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import AppModal from "../../../Components/Modals/AppModal/AppModal";
import {
  ClientMeal, MealItem, MealType,
  MEAL_TYPE_LABEL, MEAL_TYPE_ICON,
  getMeals, createMeal, updateMeal, deleteMeal,
} from "../../../utils/api/mealsAPI";
import {
  ClientSupplier, MealSupplierLink, SupplierType,
  SUPPLIER_TYPE_LABEL, SUPPLIER_TYPE_ICON,
  getSuppliers, createSupplier, updateSupplier, deleteSupplier,
} from "../../../utils/api/suppliersAPI";
import {
  ClientApartment, updateClientApartment,
} from "../../../utils/api/apartmentRentalsAPI";

interface Props {
  clientId: string;
  apartments: ClientApartment[];
  onApartmentsChange: (updated: ClientApartment) => void;
}

const ChevDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const ForkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
    <path d="M7 2v20" />
    <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
  </svg>
);

const TruckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" rx="1" />
    <path d="M16 8h4l3 3v5h-7V8z" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const fmtAmount = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${n % 1 === 0 ? n : n.toFixed(2)}`;
  if (currency === "BAM") return `${n} KM`;
  return `${n} RSD`;
};

// ─── Meal form ────────────────────────────────────────────────────────────────

const emptyMealForm = {
  name: "",
  type: "breakfast" as MealType,
  description: "",
  currency: "EUR" as "EUR" | "RSD" | "BAM",
  isActive: true,
};

const emptyRule = (): string => "";

const emptyItem = (): MealItem => ({ name: "", price: 0, isAvailable: true });

type MealFormData = typeof emptyMealForm;
type MealErrors = Partial<Record<"name" | "items", string>>;

// ─── Supplier form ────────────────────────────────────────────────────────────

const emptySupplierForm = {
  name: "",
  type: "other" as SupplierType,
  phone: "",
  note: "",
  isActive: true,
};

type SupplierFormData = typeof emptySupplierForm;
type SupplierErrors = Partial<Record<"name", string>>;

// ─── Main Component ───────────────────────────────────────────────────────────

const MealsTab: React.FC<Props> = ({ clientId, apartments, onApartmentsChange }) => {
  // Meals state
  const [meals, setMeals] = useState<ClientMeal[]>([]);
  const [mealsLoading, setMealsLoading] = useState(false);
  const [mealModalOpen, setMealModalOpen] = useState(false);
  const [editMeal, setEditMeal] = useState<ClientMeal | null>(null);
  const [mealSaving, setMealSaving] = useState(false);
  const [mealForm, setMealForm] = useState<MealFormData>(emptyMealForm);
  const [items, setItems] = useState<MealItem[]>([emptyItem()]);
  const [extras, setExtras] = useState<MealItem[]>([]);
  const [orderingRules, setOrderingRules] = useState<string[]>([]);
  const [mealErrors, setMealErrors] = useState<MealErrors>({});
  // Supplier links per meal (in edit modal)
  const [mealSupplierLinks, setMealSupplierLinks] = useState<MealSupplierLink[]>([]);

  // Suppliers state
  const [suppliers, setSuppliers] = useState<ClientSupplier[]>([]);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState<ClientSupplier | null>(null);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [supplierForm, setSupplierForm] = useState<SupplierFormData>(emptySupplierForm);
  const [supplierErrors, setSupplierErrors] = useState<SupplierErrors>({});

  // Apt-meal config
  const [aptSaving, setAptSaving] = useState<string | null>(null);

  // Active sub-section
  const [section, setSection] = useState<"meals" | "suppliers">("meals");

  const loadMeals = async () => {
    setMealsLoading(true);
    try {
      const res = await getMeals(clientId);
      setMeals(res.data);
    } catch {
      message.error("Greška pri učitavanju obroka");
    } finally {
      setMealsLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const res = await getSuppliers(clientId);
      setSuppliers(res.data);
    } catch {
      message.error("Greška pri učitavanju dobavljača");
    }
  };

  useEffect(() => {
    loadMeals();
    loadSuppliers();
  }, [clientId]);

  // ─── Meal handlers ──────────────────────────────────────────────────────────

  const setMealField = (key: keyof MealFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setMealForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (mealErrors[key as keyof MealErrors]) setMealErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateMeal = (): MealErrors => {
    const e: MealErrors = {};
    if (!mealForm.name.trim()) e.name = "Naziv je obavezan";
    if (!items.filter((it) => it.name.trim()).length) e.items = "Dodajte barem jednu stavku";
    return e;
  };

  const openCreateMeal = () => {
    setEditMeal(null);
    setMealForm(emptyMealForm);
    setItems([emptyItem()]);
    setExtras([]);
    setOrderingRules([]);
    setMealSupplierLinks([]);
    setMealErrors({});
    setMealModalOpen(true);
  };

  const openEditMeal = (meal: ClientMeal) => {
    setEditMeal(meal);
    setMealForm({
      name: meal.name,
      type: meal.type,
      description: meal.description || "",
      currency: meal.currency,
      isActive: meal.isActive,
    });
    setItems(meal.items.length ? meal.items.map((it) => ({ ...it })) : [emptyItem()]);
    setExtras(meal.extras ? meal.extras.map((it) => ({ ...it })) : []);
    setOrderingRules(meal.orderingRules ? [...meal.orderingRules] : []);
    setMealSupplierLinks(meal.suppliers ? meal.suppliers.map((s) => ({ ...s })) : []);
    setMealErrors({});
    setMealModalOpen(true);
  };

  const handleSaveMeal = async () => {
    const e = validateMeal();
    if (Object.keys(e).length > 0) { setMealErrors(e); return; }
    setMealSaving(true);
    try {
      const validItems = items
        .filter((it) => it.name.trim())
        .map((it) => ({ ...it, price: Number(it.price) || 0 }));
      const validExtras = extras
        .filter((it) => it.name.trim())
        .map((it) => ({ ...it, price: Number(it.price) || 0 }));
      const payload: Partial<ClientMeal> = {
        name: mealForm.name.trim(),
        type: mealForm.type,
        description: mealForm.description.trim() || undefined,
        currency: mealForm.currency,
        isActive: mealForm.isActive,
        items: validItems,
        extras: validExtras,
        suppliers: mealSupplierLinks,
        orderingRules: orderingRules.filter((r) => r.trim()),
      };
      if (editMeal) {
        const res = await updateMeal(editMeal._id, payload);
        setMeals((prev) => prev.map((m) => m._id === editMeal._id ? res.data : m));
        message.success("Obrok ažuriran");
      } else {
        const res = await createMeal(clientId, payload);
        setMeals((prev) => [res.data, ...prev]);
        message.success("Obrok kreiran");
      }
      setMealModalOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Greška pri čuvanju");
    } finally {
      setMealSaving(false);
    }
  };

  const handleDeleteMeal = async (id: string) => {
    try {
      await deleteMeal(id);
      setMeals((prev) => prev.filter((m) => m._id !== id));
      message.success("Obrok uklonjen");
    } catch {
      message.error("Greška pri brisanju");
    }
  };

  const handleToggleMealActive = async (meal: ClientMeal) => {
    try {
      const res = await updateMeal(meal._id, { isActive: !meal.isActive });
      setMeals((prev) => prev.map((m) => m._id === meal._id ? res.data : m));
    } catch {
      message.error("Greška");
    }
  };

  // Toggle supplier link on a meal (quick toggle from card, outside modal)
  const handleToggleMealSupplierActive = async (meal: ClientMeal, supplierName: string) => {
    const updatedSuppliers = (meal.suppliers || []).map((s) =>
      s.name === supplierName ? { ...s, isActive: !s.isActive } : s
    );
    try {
      const res = await updateMeal(meal._id, { suppliers: updatedSuppliers } as any);
      setMeals((prev) => prev.map((m) => m._id === meal._id ? res.data : m));
    } catch {
      message.error("Greška");
    }
  };

  const updateItem = (i: number, patch: Partial<MealItem>) => {
    setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    if (mealErrors.items) setMealErrors((prev) => ({ ...prev, items: undefined }));
  };

  const updateExtra = (i: number, patch: Partial<MealItem>) => {
    setExtras((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  };

  // ─── Supplier link helpers (in meal modal) ──────────────────────────────────

  const isMealLinkedToSupplier = (supplierId: string) =>
    mealSupplierLinks.some((s) => {
      const id = typeof s.supplierId === "string" ? s.supplierId : (s.supplierId as ClientSupplier)._id;
      return id === supplierId;
    });

  const toggleMealSupplierLink = (supplier: ClientSupplier) => {
    const linked = isMealLinkedToSupplier(supplier._id);
    if (linked) {
      setMealSupplierLinks((prev) => prev.filter((s) => {
        const id = typeof s.supplierId === "string" ? s.supplierId : (s.supplierId as ClientSupplier)._id;
        return id !== supplier._id;
      }));
    } else {
      setMealSupplierLinks((prev) => [
        ...prev,
        { supplierId: supplier._id, name: supplier.name, isActive: true },
      ]);
    }
  };

  const toggleMealSupplierLinkActive = (supplierId: string) => {
    setMealSupplierLinks((prev) =>
      prev.map((s) => {
        const id = typeof s.supplierId === "string" ? s.supplierId : (s.supplierId as ClientSupplier)._id;
        return id === supplierId ? { ...s, isActive: !s.isActive } : s;
      })
    );
  };

  const getMealSupplierLinkActive = (supplierId: string) =>
    mealSupplierLinks.find((s) => {
      const id = typeof s.supplierId === "string" ? s.supplierId : (s.supplierId as ClientSupplier)._id;
      return id === supplierId;
    })?.isActive ?? true;

  // ─── Supplier handlers ──────────────────────────────────────────────────────

  const setSupplierField = (key: keyof SupplierFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setSupplierForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (supplierErrors[key as keyof SupplierErrors]) setSupplierErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateSupplier = (): SupplierErrors => {
    const e: SupplierErrors = {};
    if (!supplierForm.name.trim()) e.name = "Naziv je obavezan";
    return e;
  };

  const openCreateSupplier = () => {
    setEditSupplier(null);
    setSupplierForm(emptySupplierForm);
    setSupplierErrors({});
    setSupplierModalOpen(true);
  };

  const openEditSupplier = (s: ClientSupplier) => {
    setEditSupplier(s);
    setSupplierForm({
      name: s.name,
      type: s.type,
      phone: s.phone || "",
      note: s.note || "",
      isActive: s.isActive,
    });
    setSupplierErrors({});
    setSupplierModalOpen(true);
  };

  const handleSaveSupplier = async () => {
    const e = validateSupplier();
    if (Object.keys(e).length > 0) { setSupplierErrors(e); return; }
    setSupplierSaving(true);
    try {
      const payload: Partial<ClientSupplier> = {
        name: supplierForm.name.trim(),
        type: supplierForm.type,
        phone: supplierForm.phone.trim() || undefined,
        note: supplierForm.note.trim() || undefined,
        isActive: supplierForm.isActive,
      };
      if (editSupplier) {
        const res = await updateSupplier(editSupplier._id, payload);
        setSuppliers((prev) => prev.map((s) => s._id === editSupplier._id ? res.data : s));
        message.success("Dobavljač ažuriran");
      } else {
        const res = await createSupplier(clientId, payload);
        setSuppliers((prev) => [res.data, ...prev]);
        message.success("Dobavljač dodan");
      }
      setSupplierModalOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Greška pri čuvanju");
    } finally {
      setSupplierSaving(false);
    }
  };

  const handleDeleteSupplier = async (id: string) => {
    try {
      await deleteSupplier(id);
      setSuppliers((prev) => prev.filter((s) => s._id !== id));
      message.success("Dobavljač uklonjen");
    } catch {
      message.error("Greška pri brisanju");
    }
  };

  const handleToggleSupplierActive = async (s: ClientSupplier) => {
    try {
      const res = await updateSupplier(s._id, { isActive: !s.isActive });
      setSuppliers((prev) => prev.map((sup) => sup._id === s._id ? res.data : sup));
    } catch {
      message.error("Greška");
    }
  };

  // ─── Apartment-meal configuration ──────────────────────────────────────────

  const toggleAptMeal = async (apt: ClientApartment, mealId: string) => {
    const alreadyLinked = apt.meals?.some(
      (m) => (typeof m.mealId === "string" ? m.mealId : (m.mealId as ClientMeal)._id) === mealId
    );
    const updatedMeals = alreadyLinked
      ? (apt.meals || []).filter(
          (m) => (typeof m.mealId === "string" ? m.mealId : (m.mealId as ClientMeal)._id) !== mealId
        )
      : [...(apt.meals || []), { mealId, includedInPrice: false }];

    setAptSaving(apt._id + mealId);
    try {
      const res = await updateClientApartment(apt._id, { meals: updatedMeals } as any);
      onApartmentsChange(res.data);
    } catch {
      message.error("Greška");
    } finally {
      setAptSaving(null);
    }
  };

  const toggleAptMealIncluded = async (apt: ClientApartment, mealId: string) => {
    const updatedMeals = (apt.meals || []).map((m) => {
      const id = typeof m.mealId === "string" ? m.mealId : (m.mealId as ClientMeal)._id;
      return id === mealId ? { ...m, includedInPrice: !m.includedInPrice } : m;
    });
    setAptSaving(apt._id + mealId + "inc");
    try {
      const res = await updateClientApartment(apt._id, { meals: updatedMeals } as any);
      onApartmentsChange(res.data);
    } catch {
      message.error("Greška");
    } finally {
      setAptSaving(null);
    }
  };

  const setAptMealPricePerPerson = async (apt: ClientApartment, mealId: string, price: number | undefined) => {
    const updatedMeals = (apt.meals || []).map((m) => {
      const id = typeof m.mealId === "string" ? m.mealId : (m.mealId as ClientMeal)._id;
      return id === mealId ? { ...m, pricePerPerson: price } : m;
    });
    setAptSaving(apt._id + mealId + "ppp");
    try {
      const res = await updateClientApartment(apt._id, { meals: updatedMeals } as any);
      onApartmentsChange(res.data);
    } catch {
      message.error("Greška");
    } finally {
      setAptSaving(null);
    }
  };

  const activeMeals = meals.filter((m) => m.isActive);
  const inactiveMeals = meals.filter((m) => !m.isActive);
  const activeSuppliers = suppliers.filter((s) => s.isActive);
  const inactiveSuppliers = suppliers.filter((s) => !s.isActive);

  return (
    <div style={{ padding: "16px 0" }}>

      {/* ─ Sub-nav ─ */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {(["meals", "suppliers"] as const).map((key) => {
          const label = key === "meals" ? "🍽️ Obroci" : "🚚 Dobavljači";
          const count = key === "meals" ? meals.length : suppliers.length;
          return (
            <button
              key={key}
              onClick={() => setSection(key)}
              style={{
                padding: "7px 16px",
                borderRadius: 20,
                border: `1.5px solid ${section === key ? "#5b50d6" : "#e8e5f0"}`,
                background: section === key ? "#5b50d6" : "#fff",
                color: section === key ? "#fff" : "#6b6880",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {label}
              {count > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  background: section === key ? "rgba(255,255,255,0.25)" : "#f0edf8",
                  color: section === key ? "#fff" : "#5b50d6",
                  padding: "1px 6px",
                  borderRadius: 10,
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════
          SECTION: Obroci
      ══════════════════════════════════════════ */}
      {section === "meals" && (
        <>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1520" }}>Obroci i meni</div>
              <div style={{ fontSize: 12, color: "#9893a8", marginTop: 2 }}>
                Kreirajte obroke koje nudite gostima — doručak, ručak, večera ili snack
              </div>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateMeal}
              style={{ background: "#1a1520", borderColor: "#1a1520" }}>
              Novi obrok
            </Button>
          </div>

          {/* Empty */}
          {meals.length === 0 && !mealsLoading && (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#9893a8" }}>
              <div style={{ fontSize: 42, marginBottom: 12 }}>🍽️</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1520", marginBottom: 6 }}>Nema obroka</div>
              <div style={{ fontSize: 13, marginBottom: 20 }}>
                Kreirajte npr. "Doručak" sa stavkama poput jaja, palačinki i kafe
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateMeal}
                style={{ background: "#1a1520", borderColor: "#1a1520" }}>
                Kreiraj prvi obrok
              </Button>
            </div>
          )}

          {/* Active meals */}
          {activeMeals.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#5b50d6", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                Aktivni ({activeMeals.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                {activeMeals.map((meal) => (
                  <MealCard key={meal._id} meal={meal}
                    onEdit={openEditMeal} onDelete={handleDeleteMeal}
                    onToggle={handleToggleMealActive}
                    onToggleSupplier={handleToggleMealSupplierActive}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Inactive meals */}
          {inactiveMeals.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                Neaktivni ({inactiveMeals.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, opacity: 0.65 }}>
                {inactiveMeals.map((meal) => (
                  <MealCard key={meal._id} meal={meal}
                    onEdit={openEditMeal} onDelete={handleDeleteMeal}
                    onToggle={handleToggleMealActive}
                    onToggleSupplier={handleToggleMealSupplierActive}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Apartment config */}
          {meals.length > 0 && apartments.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1520", marginBottom: 4 }}>
                Konfiguracija po apartmanima
              </div>
              <div style={{ fontSize: 12, color: "#9893a8", marginBottom: 16 }}>
                Odaberite koji obroci su dostupni u svakom apartmanu i da li su uključeni u cijenu
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {apartments.map((apt) => (
                  <ApartmentMealConfig key={apt._id} apt={apt} meals={meals}
                    aptSaving={aptSaving} onToggle={toggleAptMeal} onToggleIncluded={toggleAptMealIncluded}
                    onSetPricePerPerson={setAptMealPricePerPerson} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════
          SECTION: Dobavljači
      ══════════════════════════════════════════ */}
      {section === "suppliers" && (
        <>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1520" }}>Dobavljači obroka</div>
              <div style={{ fontSize: 12, color: "#9893a8", marginTop: 2 }}>
                Definiši ko priprema obroke — vlastita kuhinja, restoran, catering...
              </div>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateSupplier}
              style={{ background: "#1a1520", borderColor: "#1a1520" }}>
              Novi dobavljač
            </Button>
          </div>

          {/* Info box about supplier-meal linking */}
          {suppliers.length > 0 && meals.length > 0 && (
            <div style={{
              padding: "10px 14px", borderRadius: 10, marginBottom: 20,
              background: "rgba(91,80,214,0.06)", border: "1px solid rgba(91,80,214,0.15)",
              fontSize: 12.5, color: "#5b50d6", display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 15, flexShrink: 0 }}>💡</span>
              <span>
                Linkujte dobavljače na obroke u <strong>uređivanju obroka</strong>.
                Svaki obrok može imati više dobavljača — i možeš brzo prebaciti koji je aktivan.
              </span>
            </div>
          )}

          {/* Empty */}
          {suppliers.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#9893a8" }}>
              <div style={{ fontSize: 42, marginBottom: 12 }}>🚚</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1520", marginBottom: 6 }}>Nema dobavljača</div>
              <div style={{ fontSize: 13, marginBottom: 20 }}>
                Dodajte npr. "Ja – vlastita kuhinja", "Restoran Aura", "Catering Breza"
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateSupplier}
                style={{ background: "#1a1520", borderColor: "#1a1520" }}>
                Dodaj prvog dobavljača
              </Button>
            </div>
          )}

          {/* Active suppliers */}
          {activeSuppliers.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#5b50d6", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                Aktivni ({activeSuppliers.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                {activeSuppliers.map((s) => (
                  <SupplierCard key={s._id} supplier={s}
                    onEdit={openEditSupplier} onDelete={handleDeleteSupplier} onToggle={handleToggleSupplierActive}
                    mealsUsing={meals.filter((m) => m.suppliers?.some((sl) => {
                      const id = typeof sl.supplierId === "string" ? sl.supplierId : (sl.supplierId as ClientSupplier)._id;
                      return id === s._id;
                    }))}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Inactive suppliers */}
          {inactiveSuppliers.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                Neaktivni ({inactiveSuppliers.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, opacity: 0.65 }}>
                {inactiveSuppliers.map((s) => (
                  <SupplierCard key={s._id} supplier={s}
                    onEdit={openEditSupplier} onDelete={handleDeleteSupplier} onToggle={handleToggleSupplierActive}
                    mealsUsing={meals.filter((m) => m.suppliers?.some((sl) => {
                      const id = typeof sl.supplierId === "string" ? sl.supplierId : (sl.supplierId as ClientSupplier)._id;
                      return id === s._id;
                    }))}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════
          MODAL: Meal
      ══════════════════════════════════════════ */}
      <AppModal
        open={mealModalOpen}
        onClose={() => setMealModalOpen(false)}
        icon={<ForkIcon />}
        eyebrow="Obroci i meni"
        title={editMeal ? "Izmijeni obrok" : "Novi obrok"}
        description="Definiši naziv, tip, stavke i dobavljače."
        onSubmit={handleSaveMeal}
        submitLabel={mealSaving ? "Čuvanje..." : editMeal ? "Sačuvaj izmjene" : "Kreiraj obrok"}
        submitDisabled={mealSaving}
        maxWidth={560}
      >
        {/* Osnovno */}
        <div className="am-section">
          <div className="am-section-label">Osnovno</div>
          <div className="am-form-grid">
            <div className="am-field am-span-2">
              <label className="am-label">Naziv obroka <span className="am-req">*</span></label>
              <input
                className={`am-input${mealErrors.name ? " am-input-error" : ""}`}
                placeholder="npr. Doručak, Ručak, Večera..."
                value={mealForm.name}
                onChange={setMealField("name")}
              />
              {mealErrors.name && <span className="am-error-msg">{mealErrors.name}</span>}
            </div>

            <div className="am-field">
              <label className="am-label">Tip</label>
              <div className="am-select-wrap">
                <select className="am-select" value={mealForm.type} onChange={setMealField("type")}>
                  {(Object.keys(MEAL_TYPE_LABEL) as MealType[]).map((t) => (
                    <option key={t} value={t}>
                      {MEAL_TYPE_ICON[t]} {MEAL_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>

            <div className="am-field">
              <label className="am-label">Valuta</label>
              <div className="am-select-wrap">
                <select className="am-select" value={mealForm.currency} onChange={setMealField("currency")}>
                  <option value="EUR">EUR (€)</option>
                  <option value="RSD">RSD</option>
                  <option value="BAM">BAM (KM)</option>
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">
                Opis <span className="am-field-hint">opciono</span>
              </label>
              <textarea
                className="am-textarea" rows={2}
                placeholder="Kratki opis obroka za goste..."
                value={mealForm.description}
                onChange={setMealField("description")}
              />
            </div>
          </div>
        </div>

        {/* Stavke menija */}
        <div className="am-section">
          <div className="am-section-label">Stavke menija <span className="am-req">*</span></div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 40px 36px", gap: 8, marginBottom: 6 }}>
            {["Naziv stavke", "Cijena", "Dost.", ""].map((h) => (
              <span key={h} style={{ fontSize: 10.5, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</span>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 40px 36px", gap: 8, alignItems: "center" }}>
                  <input
                    className={`am-input${mealErrors.items ? " am-input-error" : ""}`}
                    placeholder={i === 0 ? "npr. Jaja sa slaninom" : i === 1 ? "npr. Komplet lepinja" : "npr. Palačinke"}
                    value={item.name}
                    onChange={(e) => updateItem(i, { name: e.target.value })}
                  />
                  <input
                    className="am-input"
                    type="number" min={0} step={0.5}
                    placeholder="0"
                    value={item.price === 0 ? "" : item.price}
                    onChange={(e) => updateItem(i, { price: parseFloat(e.target.value) || 0 })}
                  />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Switch size="small" checked={item.isAvailable} onChange={(c) => updateItem(i, { isAvailable: c })} />
                  </div>
                  {items.length > 1 ? (
                    <button type="button" onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{ width: 36, height: 36, flexShrink: 0, border: "1px solid #fecaca", borderRadius: 9, background: "#fff5f5", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                      −
                    </button>
                  ) : <span />}
                </div>
                {/* Per-item description */}
                <input
                  className="am-input"
                  style={{ fontSize: 12, color: "#6b6880", paddingRight: 184 }}
                  placeholder="Opis stavke — npr. sa kajmakom, jajima i slaninom (opciono)"
                  value={item.description || ""}
                  onChange={(e) => updateItem(i, { description: e.target.value || null })}
                />
              </div>
            ))}
            {mealErrors.items && <span className="am-error-msg">{mealErrors.items}</span>}
            <button type="button" onClick={() => setItems((prev) => [...prev, emptyItem()])}
              style={{ display: "flex", alignItems: "center", gap: 6, height: 38, padding: "0 14px", border: "1.5px dashed #c4bfef", borderRadius: 9, background: "transparent", color: "#5b50d6", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit", marginTop: 2 }}>
              + Dodaj stavku
            </button>
          </div>
        </div>

        {/* Dodatne stavke (extras) */}
        <div className="am-section">
          <div className="am-section-label">
            Dodatne stavke
            <span className="am-field-hint">opciono — gost ih može dodatno čekirati uz glavni izbor, npr. jogurt, kolač</span>
          </div>

          {extras.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 40px 36px", gap: 8, marginBottom: 6 }}>
              {["Naziv dodatka", "Cijena", "Dost.", ""].map((h) => (
                <span key={h} style={{ fontSize: 10.5, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</span>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {extras.map((extra, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 40px 36px", gap: 8, alignItems: "center" }}>
                  <input
                    className="am-input"
                    placeholder={i === 0 ? "npr. Jogurt" : i === 1 ? "npr. Kolač" : "npr. Sok"}
                    value={extra.name}
                    onChange={(e) => updateExtra(i, { name: e.target.value })}
                  />
                  <input
                    className="am-input"
                    type="number" min={0} step={0.5}
                    placeholder="0"
                    value={extra.price === 0 ? "" : extra.price}
                    onChange={(e) => updateExtra(i, { price: parseFloat(e.target.value) || 0 })}
                  />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Switch size="small" checked={extra.isAvailable} onChange={(c) => updateExtra(i, { isAvailable: c })} />
                  </div>
                  <button type="button" onClick={() => setExtras((prev) => prev.filter((_, idx) => idx !== i))}
                    style={{ width: 36, height: 36, flexShrink: 0, border: "1px solid #fecaca", borderRadius: 9, background: "#fff5f5", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                    −
                  </button>
                </div>
                <input
                  className="am-input"
                  style={{ fontSize: 12, color: "#6b6880", paddingRight: 184 }}
                  placeholder="Opis dodatka (opciono)"
                  value={extra.description || ""}
                  onChange={(e) => updateExtra(i, { description: e.target.value || null })}
                />
              </div>
            ))}
            <button type="button" onClick={() => setExtras((prev) => [...prev, emptyItem()])}
              style={{ display: "flex", alignItems: "center", gap: 6, height: 38, padding: "0 14px", border: "1.5px dashed #c4bfef", borderRadius: 9, background: "transparent", color: "#5b50d6", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit", marginTop: 2 }}>
              + Dodaj dodatnu stavku
            </button>
          </div>
        </div>

        {/* Pravila poručivanja */}
        <div className="am-section">
          <div className="am-section-label">
            Pravila poručivanja
            <span className="am-field-hint">opciono — prikazuju se gostima pri naručivanju</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {orderingRules.map((rule, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#9893a8", flexShrink: 0 }}>ℹ</span>
                <input
                  className="am-input"
                  placeholder={
                    i === 0 ? "npr. Naručite do 21h prethodnog dana" :
                    i === 1 ? "npr. Minimalna narudžba: 1 porcija po gostu" :
                    "npr. Dostava na sobu između 08:00–10:00"
                  }
                  value={rule}
                  onChange={(e) => setOrderingRules((prev) => prev.map((r, idx) => idx === i ? e.target.value : r))}
                />
                <button
                  type="button"
                  onClick={() => setOrderingRules((prev) => prev.filter((_, idx) => idx !== i))}
                  style={{
                    width: 30, height: 30, flexShrink: 0,
                    border: "1px solid #fecaca", borderRadius: 8,
                    background: "#fff5f5", color: "#dc2626",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}
                >
                  −
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setOrderingRules((prev) => [...prev, ""])}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                height: 36, padding: "0 14px",
                border: "1.5px dashed #c4bfef", borderRadius: 9,
                background: "transparent", color: "#5b50d6",
                cursor: "pointer", fontSize: 13, fontWeight: 600,
                fontFamily: "inherit", alignSelf: "flex-start",
              }}
            >
              + Dodaj pravilo
            </button>
          </div>
        </div>

        {/* Dobavljači */}
        <div className="am-section">
          <div className="am-section-label">
            Dobavljači
            <span className="am-field-hint">opciono — ko priprema ovaj obrok</span>
          </div>

          {suppliers.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "#9893a8", padding: "8px 0" }}>
              Nema definisanih dobavljača.{" "}
              <button
                type="button"
                onClick={() => { setMealModalOpen(false); setSection("suppliers"); openCreateSupplier(); }}
                style={{ background: "none", border: "none", color: "#5b50d6", cursor: "pointer", fontSize: 12.5, fontWeight: 600, padding: 0, fontFamily: "inherit" }}
              >
                Dodaj dobavljača →
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {suppliers.map((s) => {
                const linked = isMealLinkedToSupplier(s._id);
                const active = linked ? getMealSupplierLinkActive(s._id) : false;
                return (
                  <div key={s._id} style={{
                    display: "grid",
                    gridTemplateColumns: "40px 1fr auto",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: `1.5px solid ${linked ? (active ? "#5b50d6" : "#e8e5f0") : "#e8e5f0"}`,
                    background: linked ? (active ? "rgba(91,80,214,0.05)" : "#fafafa") : "#fff",
                    transition: "all 0.15s",
                  }}>
                    {/* Link toggle */}
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <input
                        type="checkbox"
                        checked={linked}
                        onChange={() => toggleMealSupplierLink(s)}
                        style={{ accentColor: "#5b50d6", width: 16, height: 16, cursor: "pointer" }}
                      />
                    </div>

                    {/* Info */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{SUPPLIER_TYPE_ICON[s.type]}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: linked ? "#1a1520" : "#9893a8" }}>
                          {s.name}
                        </div>
                        <div style={{ fontSize: 11, color: "#9893a8" }}>
                          {SUPPLIER_TYPE_LABEL[s.type]}
                          {s.phone && ` · ${s.phone}`}
                        </div>
                      </div>
                    </div>

                    {/* Active toggle (only when linked) */}
                    {linked && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Switch
                          size="small"
                          checked={active}
                          onChange={() => toggleMealSupplierLinkActive(s._id)}
                          style={active ? { background: "#16a34a" } : undefined}
                        />
                        <span style={{ fontSize: 11, fontWeight: 600, color: active ? "#16a34a" : "#9893a8", whiteSpace: "nowrap" }}>
                          {active ? "Aktivan" : "Neaktivan"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Postavke */}
        <div className="am-section">
          <div className="am-section-label">Postavke</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Switch
              checked={mealForm.isActive}
              onChange={(c) => setMealForm((prev) => ({ ...prev, isActive: c }))}
            />
            <span style={{ fontSize: 13.5, fontWeight: 550, color: mealForm.isActive ? "#15803d" : "#9893a8" }}>
              {mealForm.isActive ? "Obrok je aktivan" : "Obrok je neaktivan"}
            </span>
          </div>
        </div>
      </AppModal>

      {/* ══════════════════════════════════════════
          MODAL: Supplier
      ══════════════════════════════════════════ */}
      <AppModal
        open={supplierModalOpen}
        onClose={() => setSupplierModalOpen(false)}
        icon={<TruckIcon />}
        eyebrow="Dobavljači obroka"
        title={editSupplier ? "Izmijeni dobavljača" : "Novi dobavljač"}
        description="Definiši ko priprema ili dostavlja obroke."
        onSubmit={handleSaveSupplier}
        submitLabel={supplierSaving ? "Čuvanje..." : editSupplier ? "Sačuvaj izmjene" : "Dodaj dobavljača"}
        submitDisabled={supplierSaving}
        maxWidth={460}
      >
        <div className="am-section">
          <div className="am-section-label">Osnovno</div>
          <div className="am-form-grid">
            <div className="am-field am-span-2">
              <label className="am-label">Naziv <span className="am-req">*</span></label>
              <input
                className={`am-input${supplierErrors.name ? " am-input-error" : ""}`}
                placeholder="npr. Ja – vlastita kuhinja, Restoran Aura..."
                value={supplierForm.name}
                onChange={setSupplierField("name")}
              />
              {supplierErrors.name && <span className="am-error-msg">{supplierErrors.name}</span>}
            </div>

            <div className="am-field">
              <label className="am-label">Tip</label>
              <div className="am-select-wrap">
                <select className="am-select" value={supplierForm.type} onChange={setSupplierField("type")}>
                  {(Object.keys(SUPPLIER_TYPE_LABEL) as SupplierType[]).map((t) => (
                    <option key={t} value={t}>
                      {SUPPLIER_TYPE_ICON[t]} {SUPPLIER_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>

            <div className="am-field">
              <label className="am-label">
                Telefon <span className="am-field-hint">opciono</span>
              </label>
              <input
                className="am-input"
                placeholder="+387 6x xxx xxx"
                value={supplierForm.phone}
                onChange={setSupplierField("phone")}
              />
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">
                Napomena <span className="am-field-hint">opciono</span>
              </label>
              <textarea
                className="am-textarea" rows={2}
                placeholder="npr. Radi pon–sub, naručiti dan ranije..."
                value={supplierForm.note}
                onChange={setSupplierField("note")}
              />
            </div>
          </div>
        </div>

        <div className="am-section">
          <div className="am-section-label">Postavke</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Switch
              checked={supplierForm.isActive}
              onChange={(c) => setSupplierForm((prev) => ({ ...prev, isActive: c }))}
            />
            <span style={{ fontSize: 13.5, fontWeight: 550, color: supplierForm.isActive ? "#15803d" : "#9893a8" }}>
              {supplierForm.isActive ? "Dobavljač je aktivan" : "Dobavljač je neaktivan"}
            </span>
          </div>
        </div>
      </AppModal>
    </div>
  );
};

// ─── Meal Card ────────────────────────────────────────────────────────────────

interface MealCardProps {
  meal: ClientMeal;
  onEdit: (m: ClientMeal) => void;
  onDelete: (id: string) => void;
  onToggle: (m: ClientMeal) => void;
  onToggleSupplier: (m: ClientMeal, supplierName: string) => void;
}

const MealCard: React.FC<MealCardProps> = ({ meal, onEdit, onDelete, onToggle, onToggleSupplier }) => {
  const availableItems = meal.items.filter((it) => it.isAvailable);
  const unavailableItems = meal.items.filter((it) => !it.isAvailable);
  const availableExtras = (meal.extras || []).filter((it) => it.isAvailable);
  const linkedSuppliers = meal.suppliers || [];

  return (
    <div style={{
      border: `1.5px solid ${meal.isActive ? "rgba(91,80,214,0.2)" : "#e8e5f0"}`,
      borderRadius: 14,
      padding: "16px 16px 14px",
      background: meal.isActive ? "linear-gradient(135deg,#f8f7ff 0%,#fff 100%)" : "#fafafa",
      position: "relative",
      overflow: "hidden",
    }}>
      {meal.isActive && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#5b50d6,#818cf8)", borderRadius: "14px 14px 0 0" }} />
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 18 }}>{MEAL_TYPE_ICON[meal.type]}</span>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: meal.isActive ? "#5b50d6" : "#9893a8" }}>
              {MEAL_TYPE_LABEL[meal.type]}
            </span>
          </div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1520" }}>{meal.name}</div>
          {meal.description && (
            <div style={{ fontSize: 11.5, color: "#6b6880", marginTop: 3, lineHeight: 1.4 }}>{meal.description}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 5, flexShrink: 0, alignItems: "center" }}>
          <Tooltip title={meal.isActive ? "Deaktiviraj" : "Aktiviraj"}>
            <Switch checked={meal.isActive} size="small" onChange={() => onToggle(meal)} />
          </Tooltip>
          <Tooltip title="Izmijeni">
            <button onClick={() => onEdit(meal)}
              style={{ width: 28, height: 28, border: "1px solid #e8e5f0", borderRadius: 7, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b6880" }}>
              <EditOutlined style={{ fontSize: 12 }} />
            </button>
          </Tooltip>
          <Popconfirm title="Ukloniti obrok?" description="Obrok ostaje u istoriji rezervacija."
            onConfirm={() => onDelete(meal._id)} okText="Ukloni" cancelText="Otkaži" okButtonProps={{ danger: true }}>
            <button style={{ width: 28, height: 28, border: "1px solid #fecaca", borderRadius: 7, background: "#fff5f5", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
              <DeleteOutlined style={{ fontSize: 12 }} />
            </button>
          </Popconfirm>
        </div>
      </div>

      {/* Items */}
      <div style={{ borderTop: "1px solid #f0edf8", paddingTop: 10, marginBottom: linkedSuppliers.length > 0 ? 10 : 0 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
          {meal.items.length} {meal.items.length === 1 ? "stavka" : "stavke/i"}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {availableItems.slice(0, 3).map((it, idx) => (
            <div key={idx} style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "#3d3751" }}>✓ {it.name}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: it.price === 0 ? "#16a34a" : "#1a1520" }}>
                {it.price === 0 ? "ukl." : fmtAmount(it.price, meal.currency)}
              </span>
            </div>
          ))}
          {unavailableItems.length > 0 && (
            <div style={{ fontSize: 11, color: "#c4bfef", fontStyle: "italic", marginTop: 2 }}>
              {unavailableItems.length} stavka trenutno nedostupna
            </div>
          )}
          {availableItems.length > 3 && (
            <div style={{ fontSize: 11, color: "#9893a8", marginTop: 2 }}>+ {availableItems.length - 3} više...</div>
          )}
        </div>
        {availableExtras.length > 0 && (
          <div style={{ fontSize: 11.5, color: "#5b50d6", marginTop: 6 }}>
            🍪 {availableExtras.length} dodatn{availableExtras.length === 1 ? "a stavka" : "e stavke"}: {availableExtras.map((e) => e.name).join(", ")}
          </div>
        )}
      </div>

      {/* Suppliers */}
      {linkedSuppliers.length > 0 && (
        <div style={{ borderTop: "1px solid #f0edf8", paddingTop: 10 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 7 }}>
            Dobavljači
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {linkedSuppliers.map((s, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 12.5, color: s.isActive ? "#1a1520" : "#9893a8", fontWeight: s.isActive ? 600 : 400 }}>
                  {s.isActive ? "🟢" : "🔴"} {s.name}
                </span>
                <Switch
                  size="small"
                  checked={s.isActive}
                  onChange={() => onToggleSupplier(meal, s.name)}
                  style={s.isActive ? { background: "#16a34a" } : undefined}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Supplier Card ────────────────────────────────────────────────────────────

interface SupplierCardProps {
  supplier: ClientSupplier;
  onEdit: (s: ClientSupplier) => void;
  onDelete: (id: string) => void;
  onToggle: (s: ClientSupplier) => void;
  mealsUsing: ClientMeal[];
}

const SupplierCard: React.FC<SupplierCardProps> = ({ supplier, onEdit, onDelete, onToggle, mealsUsing }) => (
  <div style={{
    border: `1.5px solid ${supplier.isActive ? "rgba(91,80,214,0.2)" : "#e8e5f0"}`,
    borderRadius: 12,
    padding: "14px 14px 12px",
    background: supplier.isActive ? "linear-gradient(135deg,#f8f7ff 0%,#fff 100%)" : "#fafafa",
    position: "relative",
    overflow: "hidden",
  }}>
    {supplier.isActive && (
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#16a34a,#4ade80)", borderRadius: "12px 12px 0 0" }} />
    )}

    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 20 }}>{SUPPLIER_TYPE_ICON[supplier.type]}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1520" }}>{supplier.name}</div>
            <div style={{ fontSize: 11, color: "#9893a8" }}>
              {SUPPLIER_TYPE_LABEL[supplier.type]}
              {supplier.phone && <> · <a href={`tel:${supplier.phone}`} style={{ color: "#5b50d6" }}>{supplier.phone}</a></>}
            </div>
          </div>
        </div>
        {supplier.note && (
          <div style={{ fontSize: 11.5, color: "#6b6880", marginLeft: 26, marginTop: 2, lineHeight: 1.4 }}>
            {supplier.note}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 5, flexShrink: 0, alignItems: "center" }}>
        <Tooltip title={supplier.isActive ? "Deaktiviraj" : "Aktiviraj"}>
          <Switch checked={supplier.isActive} size="small" onChange={() => onToggle(supplier)} />
        </Tooltip>
        <Tooltip title="Izmijeni">
          <button onClick={() => onEdit(supplier)}
            style={{ width: 28, height: 28, border: "1px solid #e8e5f0", borderRadius: 7, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b6880" }}>
            <EditOutlined style={{ fontSize: 12 }} />
          </button>
        </Tooltip>
        <Popconfirm title="Ukloniti dobavljača?" description="Biće uklonjen s liste, ali ostaje u istoriji."
          onConfirm={() => onDelete(supplier._id)} okText="Ukloni" cancelText="Otkaži" okButtonProps={{ danger: true }}>
          <button style={{ width: 28, height: 28, border: "1px solid #fecaca", borderRadius: 7, background: "#fff5f5", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
            <DeleteOutlined style={{ fontSize: 12 }} />
          </button>
        </Popconfirm>
      </div>
    </div>

    {/* Meals using this supplier */}
    {mealsUsing.length > 0 && (
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f0edf8" }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>
          Koristi se za
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {mealsUsing.map((m) => {
            const link = m.suppliers?.find((s) => {
              const id = typeof s.supplierId === "string" ? s.supplierId : (s.supplierId as ClientSupplier)._id;
              return id === supplier._id;
            });
            return (
              <span key={m._id} style={{
                fontSize: 11, fontWeight: 600, padding: "2px 8px",
                borderRadius: 20,
                background: link?.isActive ? "rgba(22,163,74,0.1)" : "rgba(156,163,175,0.12)",
                color: link?.isActive ? "#16a34a" : "#9893a8",
                border: `1px solid ${link?.isActive ? "rgba(22,163,74,0.2)" : "#e8e5f0"}`,
              }}>
                {MEAL_TYPE_ICON[m.type]} {m.name}
                {link && !link.isActive && " (off)"}
              </span>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

// ─── Apartment Meal Config ────────────────────────────────────────────────────

interface AptConfigProps {
  apt: ClientApartment;
  meals: ClientMeal[];
  aptSaving: string | null;
  onToggle: (apt: ClientApartment, mealId: string) => void;
  onToggleIncluded: (apt: ClientApartment, mealId: string) => void;
  onSetPricePerPerson: (apt: ClientApartment, mealId: string, price: number | undefined) => void;
}

const ApartmentMealConfig: React.FC<AptConfigProps> = ({ apt, meals, aptSaving, onToggle, onToggleIncluded, onSetPricePerPerson }) => {
  const getMealId = (m: ClientMeal["_id"] | ClientMeal) =>
    typeof m === "string" ? m : m._id;

  const linkedMeals = (apt.meals || []).map((am) => ({
    id: getMealId(am.mealId as any),
    includedInPrice: am.includedInPrice,
    pricePerPerson: (am as any).pricePerPerson as number | undefined,
  }));

  const [showQr, setShowQr] = React.useState(false);
  const menuUrl = `${window.location.origin}/menu/${apt._id}`;

  const handlePrint = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const svgEl = document.getElementById(`qr-svg-${apt._id}`);
    const svgStr = svgEl ? svgEl.outerHTML : "";
    win.document.write(`
      <html><head><title>QR Meni — ${apt.name}</title>
      <style>
        body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; background: #fff; }
        h2 { font-size: 22px; margin-bottom: 8px; color: #1a1520; }
        p { font-size: 13px; color: #6b7280; margin: 0 0 20px; }
        svg { display: block; }
        .url { margin-top: 16px; font-size: 11px; color: #9893a8; word-break: break-all; max-width: 260px; text-align: center; }
      </style></head>
      <body>
        <h2>${apt.name}</h2>
        <p>Skenirajte QR kod za naručivanje hrane</p>
        ${svgStr}
        <div class="url">${menuUrl}</div>
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const isLinked = (mealId: string) => linkedMeals.some((m) => m.id === mealId);
  const isIncluded = (mealId: string) => linkedMeals.find((m) => m.id === mealId)?.includedInPrice ?? false;
  const getPricePerPerson = (mealId: string) => linkedMeals.find((m) => m.id === mealId)?.pricePerPerson;
  const activeMeals = meals.filter((m) => m.isActive);

  const [localPrices, setLocalPrices] = React.useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    linkedMeals.forEach((m) => {
      if (m.pricePerPerson != null) init[m.id] = String(m.pricePerPerson);
    });
    return init;
  });

  const isDirty = (mealId: string) => {
    const raw = localPrices[mealId];
    if (raw === undefined) return false;
    const num = parseFloat(raw);
    const saved = getPricePerPerson(mealId);
    const parsed = !isNaN(num) && num > 0 ? num : undefined;
    return parsed !== saved;
  };

  const handlePriceSave = (mealId: string) => {
    const raw = localPrices[mealId] ?? "";
    const num = parseFloat(raw);
    const price = !isNaN(num) && num > 0 ? num : undefined;
    onSetPricePerPerson(apt, mealId, price);
  };

  return (
    <div style={{ border: "1.5px solid #e8e5f0", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "10px 16px", background: "#f8f7ff", borderBottom: "1px solid #e8e5f0", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 15 }}>🏠</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#1a1520" }}>{apt.name}</span>
        <span style={{ fontSize: 11, color: "#9893a8", flex: 1 }}>{apt.location}</span>
        <button
          type="button"
          onClick={() => setShowQr(true)}
          title="QR kod za naručivanje"
          style={{
            display: "flex", alignItems: "center", gap: 5,
            padding: "4px 10px", border: "1.5px solid #7c6ef5",
            borderRadius: 7, background: "#f5f3ff", color: "#5b50d6",
            fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3h7v7H3zm1 1v5h5V4zm1 1h3v3H5zm8-2h7v7h-7zm1 1v5h5V4zm1 1h3v3h-3zm-9 8H3v7h7v-7zm-1 1v5H4v-5zm-4 1h3v3H5v-3zm10 5v-5h-1v1h-1v-1h-1v2h1v1h-1v1h1v1h1v-1h1v1h1v-2zm-1-2h-1v-1h1zm0-2v1h-1v-1zm2 4h1v1h-1zm0-2h1v1h-1zm2 0h-1v-1h1v-1h1v2zm0 2v1h-1v-1zm1-4h-1v-1h1v1z"/>
          </svg>
          QR Meni
        </button>
      </div>

      {showQr && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1100,
          }}
          onClick={() => setShowQr(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: 16, padding: 32,
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 16, boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
              maxWidth: 340, width: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1520" }}>{apt.name}</div>
            <div style={{ fontSize: 12, color: "#6b7280", textAlign: "center" }}>
              Skenirajte QR kod za naručivanje hrane
            </div>
            <QRCodeSVG
              id={`qr-svg-${apt._id}`}
              value={menuUrl}
              size={200}
              bgColor="#ffffff"
              fgColor="#1a1520"
              level="M"
            />
            <div style={{ fontSize: 10, color: "#9893a8", wordBreak: "break-all", textAlign: "center", maxWidth: 220 }}>
              {menuUrl}
            </div>
            <div style={{ display: "flex", gap: 10, width: "100%" }}>
              <button
                type="button"
                onClick={handlePrint}
                style={{
                  flex: 1, padding: "10px 0",
                  border: "1.5px solid #7c6ef5", borderRadius: 10,
                  background: "#7c6ef5", color: "#fff",
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}
              >
                Štampaj
              </button>
              <button
                type="button"
                onClick={() => setShowQr(false)}
                style={{
                  flex: 1, padding: "10px 0",
                  border: "1.5px solid #e5e2f0", borderRadius: 10,
                  background: "#f8f7ff", color: "#6b7280",
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        {activeMeals.map((meal, idx) => {
          const linked = isLinked(meal._id);
          const included = isIncluded(meal._id);
          const ppp = getPricePerPerson(meal._id);
          return (
            <div key={meal._id} style={{
              display: "grid", gridTemplateColumns: "40px 1fr auto", alignItems: "center",
              gap: 12, padding: "10px 16px",
              borderBottom: idx < activeMeals.length - 1 ? "1px solid #f0edf8" : undefined,
              background: linked ? "rgba(91,80,214,0.03)" : "#fff",
            }}>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Switch size="small" checked={linked}
                  loading={aptSaving === apt._id + meal._id}
                  onChange={() => onToggle(apt, meal._id)} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>{MEAL_TYPE_ICON[meal.type]}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: linked ? "#1a1520" : "#9893a8" }}>{meal.name}</div>
                  <div style={{ fontSize: 11, color: "#9893a8" }}>
                    {meal.items.filter((it) => it.isAvailable).length} stavki · {meal.currency}
                  </div>
                </div>
              </div>
              {linked && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                  {/* Included toggle */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Switch size="small" checked={included}
                      loading={aptSaving === apt._id + meal._id + "inc"}
                      onChange={() => onToggleIncluded(apt, meal._id)}
                      style={included ? { background: "#16a34a" } : undefined} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: included ? "#16a34a" : "#9893a8", whiteSpace: "nowrap" }}>
                      {included ? "Uključen u cijenu" : "Plaća se posebno"}
                    </span>
                  </div>
                  {/* Per-person price input — only when paid separately */}
                  {!included && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 11, color: "#9893a8" }}>
                        {meal.currency === "EUR" ? "€" : meal.currency === "BAM" ? "KM" : "RSD"}
                      </span>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        placeholder="—"
                        value={localPrices[meal._id] ?? (ppp != null ? String(ppp) : "")}
                        onChange={(e) => setLocalPrices((prev) => ({ ...prev, [meal._id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") handlePriceSave(meal._id); }}
                        style={{
                          width: 62, height: 28,
                          padding: "0 6px",
                          border: `1.5px solid ${isDirty(meal._id) ? "#f59e0b" : ppp ? "#5b50d6" : "#e8e5f0"}`,
                          borderRadius: 7,
                          fontSize: 12, fontFamily: "inherit",
                          outline: "none",
                          background: ppp && !isDirty(meal._id) ? "rgba(91,80,214,0.04)" : "#fff",
                          color: "#1a1520",
                          appearance: "none" as any,
                        }}
                      />
                      <span style={{ fontSize: 11, color: "#9893a8", whiteSpace: "nowrap" }}>/ os.</span>
                      {isDirty(meal._id) && (
                        <button
                          type="button"
                          onClick={() => handlePriceSave(meal._id)}
                          disabled={aptSaving === apt._id + meal._id + "ppp"}
                          style={{
                            height: 28, padding: "0 8px",
                            border: "1.5px solid #16a34a",
                            borderRadius: 7,
                            background: "#f0fdf4",
                            color: "#16a34a",
                            fontSize: 13, fontWeight: 700,
                            cursor: "pointer",
                            display: "flex", alignItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          ✓
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {activeMeals.length === 0 && (
          <div style={{ padding: "16px", fontSize: 12, color: "#9893a8", textAlign: "center" }}>Nema aktivnih obroka</div>
        )}
      </div>
    </div>
  );
};

export default MealsTab;
